import React, { useEffect, useMemo, useRef, useState } from 'react';
import { BsSearch } from 'react-icons/bs';
import { LuX } from 'react-icons/lu';
import { NODE_THEMES } from '../Components/NodeTheme';
import { AGENT_COLOR, TOOL_CATEGORIES, TOOL_TYPES } from './agentCatalog';

const normalize = (text) =>
  String(text || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '');

const ToolIcon = ({ meta, size = 30 }) => {
  const Icon = meta.icon;
  return (
    <span style={{ width: size, height: size, minWidth: size, borderRadius: '50%', backgroundColor: meta.color, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <Icon style={{ fontSize: size * 0.5, color: '#FFFFFF' }} />
    </span>
  );
};

const AgentToolPicker = ({ tools = [], initialType, onPick, onClose, isDark }) => {
  const dark = isDark === 'true';
  const theme = dark ? NODE_THEMES.dark : NODE_THEMES.light;
  const selectedBg = dark ? '#232B36' : '#EEF0F3';
  const hoverBg = dark ? '#1F2630' : '#F6F7F9';
  const [query, setQuery] = useState('');
  const [hovered, setHovered] = useState(null);
  const inputRef = useRef(null);
  const usedTypes = useMemo(() => new Set(tools.map((tool) => tool.type)), [tools]);

  const groups = useMemo(() => {
    const q = normalize(query.trim());
    const matches = (meta) => !q || normalize(`${meta.label} ${meta.hint} ${meta.category} ${(meta.suggestions || []).join(' ')}`).includes(q);
    return TOOL_CATEGORIES.map((category) => ({
      category,
      items: Object.entries(TOOL_TYPES)
        .filter(([, meta]) => meta.category === category && matches(meta))
        .map(([type, meta]) => ({ type, meta, taken: meta.single && usedTypes.has(type) })),
    })).filter((group) => group.items.length);
  }, [query, usedTypes]);

  const flat = useMemo(() => groups.flatMap((group) => group.items).filter((item) => !item.taken), [groups]);
  const [activeType, setActiveType] = useState(initialType || null);
  const active = flat.find((item) => item.type === activeType) || flat[0] || null;

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const onKeyDown = (event) => {
    if (event.key === 'Escape') onClose();
    if (event.key === 'Enter' && active) onPick(active.type);
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      const index = flat.findIndex((item) => item.type === active?.type);
      const next = flat[(index + (event.key === 'ArrowDown' ? 1 : -1) + flat.length) % flat.length];
      if (next) setActiveType(next.type);
    }
  };

  return (
    <div
      onMouseDown={(event) => event.target === event.currentTarget && onClose()}
      style={{ position: 'fixed', inset: 0, zIndex: 1200, backgroundColor: 'rgba(8, 11, 15, 0.6)', display: 'flex', justifyContent: 'center', alignItems: 'flex-start', paddingTop: '10vh' }}
    >
      <div
        role="dialog"
        aria-label="Agregar herramienta"
        onKeyDown={onKeyDown}
        style={{ width: '820px', maxWidth: 'calc(100vw - 32px)', height: 'min(560px, 78vh)', display: 'flex', flexDirection: 'column', backgroundColor: theme.cardBg, border: `1px solid ${theme.border}`, borderRadius: '14px', boxShadow: '0 16px 40px rgba(0, 0, 0, 0.4)', overflow: 'hidden' }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '14px 18px', borderBottom: `1px solid ${theme.border}` }}>
          <BsSearch style={{ fontSize: '15px', color: theme.textSecondary }} />
          <input
            ref={inputRef}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Busca o describe lo que necesitas: «avisar a mi sistema»"
            aria-label="Buscar herramienta"
            style={{ flex: 1, minWidth: 0, background: 'transparent', border: 'none', outline: 'none', color: theme.textPrimary, fontSize: '15px' }}
          />
          <button type="button" aria-label="Cerrar" onClick={onClose} style={{ width: '30px', height: '30px', borderRadius: '8px', border: `1px solid ${theme.border}`, background: 'transparent', color: theme.textSecondary, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
            <LuX style={{ fontSize: '14px' }} />
          </button>
        </div>

        <div style={{ display: 'flex', flex: 1, minHeight: 0 }}>
          <div style={{ flex: '1 1 340px', minWidth: 0, overflowY: 'auto', padding: '10px', borderRight: `1px solid ${theme.border}` }} onMouseLeave={() => setHovered(null)}>
            {!groups.length && <div style={{ padding: '24px', fontSize: '13px', color: theme.textSecondary, textAlign: 'center' }}>No encontramos una herramienta con ese nombre.</div>}
            {groups.map((group) => (
              <div key={group.category} style={{ marginBottom: '6px' }}>
                <div style={{ padding: '8px 10px 6px', fontSize: '11px', fontWeight: 700, letterSpacing: '0.8px', color: theme.textSecondary, textTransform: 'uppercase' }}>{group.category}</div>
                {group.items.map(({ type, meta, taken }) => {
                  const isActive = active?.type === type;
                  const isHovered = hovered === type && !isActive;
                  return (
                    <button
                      key={type}
                      type="button"
                      disabled={taken}
                      onMouseEnter={() => setHovered(type)}
                      onClick={() => setActiveType(type)}
                      onDoubleClick={() => onPick(type)}
                      title={taken ? '' : 'Doble clic para agregarla'}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        width: '100%',
                        textAlign: 'left',
                        padding: '9px 10px',
                        borderRadius: '8px',
                        border: 'none',
                        backgroundColor: isActive ? selectedBg : isHovered && !taken ? hoverBg : 'transparent',
                        color: theme.textPrimary,
                        cursor: taken ? 'default' : 'pointer',
                        opacity: taken ? 0.5 : 1,
                        transition: 'background-color 0.12s ease',
                      }}
                    >
                      <ToolIcon meta={meta} />
                      <span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '2px' }}>
                        <span style={{ fontSize: '13px', fontWeight: 600 }}>{meta.label}</span>
                        <span style={{ fontSize: '12px', color: theme.textSecondary }}>{taken ? 'Ya está en tu agente' : meta.hint}</span>
                      </span>
                    </button>
                  );
                })}
              </div>
            ))}
          </div>

          <div style={{ flex: '1 1 360px', minWidth: 0, overflowY: 'auto', padding: '22px', display: 'flex', flexDirection: 'column', gap: '18px', backgroundColor: theme.panelBg }}>
            {active ? (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <ToolIcon meta={active.meta} size={40} />
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                    <span style={{ fontSize: '17px', fontWeight: 700, color: theme.textPrimary }}>{active.meta.label}</span>
                    <span style={{ fontSize: '12px', color: theme.textSecondary }}>{active.meta.hint}</span>
                  </div>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <span style={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.8px', color: theme.textSecondary }}>ASÍ SE VE EN UNA CONVERSACIÓN</span>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', padding: '12px', borderRadius: '10px', backgroundColor: dark ? '#11161D' : '#F3F4F6', border: `1px solid ${theme.border}` }}>
                    {(active.meta.example || []).map((line, index) => {
                      if (line.from === 'tool') {
                        return (
                          <span key={index} style={{ alignSelf: 'center', display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '3px 9px', borderRadius: '6px', backgroundColor: theme.cardBg, border: `1px solid ${theme.border}`, fontSize: '11px', fontWeight: 600, color: theme.textSecondary }}>
                            <span style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: '#2F9E6E' }} />
                            {line.text}
                          </span>
                        );
                      }
                      const mine = line.from === 'user';
                      return (
                        <div key={index} style={{ alignSelf: mine ? 'flex-end' : 'flex-start', maxWidth: '85%', borderRadius: mine ? '10px 10px 2px 10px' : '10px 10px 10px 2px', backgroundColor: mine ? '#005C4B' : dark ? '#202C33' : '#FFFFFF', color: mine ? '#FFFFFF' : theme.textPrimary, fontSize: '12px', lineHeight: 1.45, overflow: 'hidden' }}>
                          <div style={{ padding: '7px 11px' }}>{line.text}</div>
                          {line.action && <div style={{ padding: '7px', borderTop: `1px solid ${theme.border}`, textAlign: 'center', color: '#53BDEB', fontWeight: 600 }}>{line.action}</div>}
                          {(line.options || []).map((option) => (
                            <div key={option} style={{ padding: '6px', borderTop: `1px solid ${theme.border}`, textAlign: 'center', color: '#53BDEB', fontWeight: 600 }}>
                              {option}
                            </div>
                          ))}
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  <span style={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.8px', color: theme.textSecondary }}>LA IA LA USARÁ</span>
                  <span style={{ fontSize: '13px', lineHeight: 1.5, color: theme.textPrimary }}>{active.meta.suggestions?.[0]}</span>
                  <span style={{ fontSize: '12px', color: theme.textSecondary }}>Puedes cambiarlo después de agregarla.</span>
                </div>
              </>
            ) : (
              <div style={{ margin: 'auto', fontSize: '13px', color: theme.textSecondary }}>Elige una herramienta para ver cómo funciona.</div>
            )}

            <div style={{ marginTop: 'auto', display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
              <button type="button" onClick={onClose} style={{ padding: '9px 16px', borderRadius: '8px', border: `1px solid ${theme.border}`, background: 'transparent', color: theme.textPrimary, fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}>
                Cancelar
              </button>
              <button type="button" disabled={!active} onClick={() => active && onPick(active.type)} style={{ padding: '9px 18px', borderRadius: '8px', border: 'none', backgroundColor: AGENT_COLOR, color: '#FFFFFF', fontSize: '13px', fontWeight: 700, cursor: active ? 'pointer' : 'default', opacity: active ? 1 : 0.5 }}>
                Agregar y configurar
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AgentToolPicker;
